import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface OnlineUser {
  id: string;
  isOnline: boolean;
}

export const useOnlineStatus = (chatId: string | null, otherUserId: string | null) => {
  const { user } = useAuthContext();
  const [isOtherUserOnline, setIsOtherUserOnline] = useState(false);
  const [otherUserLastSeen, setOtherUserLastSeen] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch last_seen from database
  const fetchLastSeen = useCallback(async () => {
    if (!otherUserId) return;

    const { data } = await supabase
      .from("profiles")
      .select("last_seen")
      .eq("id", otherUserId)
      .single();

    if (data?.last_seen) {
      setOtherUserLastSeen(data.last_seen);
    }
  }, [otherUserId]);

  // Update own last_seen
  const updateLastSeen = useCallback(async () => {
    if (!user) return;

    await supabase.rpc("update_last_seen");
  }, [user]);

  // Subscribe to presence channel for online status
  useEffect(() => {
    if (!chatId || !user || !otherUserId) return;

    const channel = supabase.channel(`online:${chatId}`, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        setIsOtherUserOnline(!!state[otherUserId]);
      })
      .on("presence", { event: "join" }, ({ key }) => {
        if (key === otherUserId) {
          setIsOtherUserOnline(true);
        }
      })
      .on("presence", { event: "leave" }, ({ key }) => {
        if (key === otherUserId) {
          setIsOtherUserOnline(false);
          fetchLastSeen(); // Refresh last seen when user goes offline
        }
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({ id: user.id, online: true });
        }
      });

    channelRef.current = channel;

    // Update our own last_seen periodically
    updateLastSeen();
    updateIntervalRef.current = setInterval(updateLastSeen, 30000); // Every 30 seconds

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, [chatId, user, otherUserId, updateLastSeen, fetchLastSeen]);

  // Initial fetch of last_seen
  useEffect(() => {
    fetchLastSeen();
  }, [fetchLastSeen]);

  // Format last seen time
  const formatLastSeen = useCallback((lastSeen: string | null): string => {
    if (!lastSeen) return "Offline";

    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return lastSeenDate.toLocaleDateString();
  }, []);

  const statusText = isOtherUserOnline 
    ? "Online now" 
    : `Last seen ${formatLastSeen(otherUserLastSeen)}`;

  return {
    isOtherUserOnline,
    otherUserLastSeen,
    statusText,
    formatLastSeen,
  };
};
